import { Post } from "../../types/api/Post";
import { format } from "date-fns";
import { getSignedUrl } from "../../api/files";
import { useState, useEffect, useRef, useCallback } from "react";
import { Calendar } from "react-feather";

interface CalendarGridProps {
  posts: Post[];
  onPostClick?: (post: Post) => void;
}

const POSTS_PER_PAGE = 20;

export function CalendarGrid({ posts, onPostClick }: CalendarGridProps) {
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [displayedCount, setDisplayedCount] = useState(POSTS_PER_PAGE);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const sortedPosts = [...posts].sort((a, b) => {
    return (
      new Date(b.publish_at!).getTime() - new Date(a.publish_at!).getTime()
    );
  });

  const displayedPosts = sortedPosts.slice(0, displayedCount);

  const loadMore = useCallback(() => {
    setDisplayedCount((prev) => {
      if (prev < sortedPosts.length) {
        return Math.min(prev + POSTS_PER_PAGE, sortedPosts.length);
      }
      return prev;
    });
  }, [sortedPosts.length]);

  useEffect(() => {
    const loadSignedUrls = async () => {
      const urls: Record<string, string> = {};

      for (const post of displayedPosts) {
        for (const media of post.posts_media || []) {
          if (media.src_thumbnail) {
            const url = await getSignedUrl(media.src_thumbnail);
            urls[media.src_thumbnail] = url;
          }
        }
      }

      setSignedUrls(urls);
    };

    loadSignedUrls();
  }, [displayedPosts.length]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore]);

  useEffect(() => {
    setDisplayedCount(POSTS_PER_PAGE);
  }, [posts]);

  const handlePhotoClick = (post: Post) => {
    if (onPostClick) {
      onPostClick(post);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
        {displayedPosts.map((post) => {
          const media = post.posts_media || [];
          if (media.length === 0) return null;

          return (
            <div
              key={post._id}
              onClick={() => handlePhotoClick(post)}
              className="card rounded-md bg-base-100 shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="card-body gap-0 p-0 group">
                <figure className="aspect-square overflow-hidden rounded-t-md items-start bg-base-200">
                  {media.length > 1 && (
                    <div className="carousel carousel-center items-center h-full">
                      {media.map((m) => (
                        <div
                          key={m.id}
                          className="carousel-item h-full w-full justify-center"
                        >
                          <img
                            className="w-full h-full object-cover group-hover:w-auto group-hover:h-auto group-hover:object-contain"
                            src={signedUrls[m?.src_thumbnail!]}
                            alt={post.posts_media?.[0]?.title || ""}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {media.length === 1 && (
                    <img
                      className="w-full h-full object-cover group-hover:w-auto group-hover:h-auto group-hover:object-contain"
                      src={signedUrls[media[0]?.src_thumbnail!]}
                      alt={post.posts_media?.[0]?.title || ""}
                    />
                  )}
                </figure>

                <div className="flex items-center gap-2 p-4">
                  <Calendar size={16} />
                  {post.publish_at &&
                    format(new Date(post.publish_at), "MMM d, yyyy")}
                </div>
              </div>
            </div>
          );
        })}

        {displayedCount < sortedPosts.length && (
          <div
            ref={loadMoreRef}
            className="col-span-2 md:col-span-4 py-4 text-center"
          >
            <span className="loading loading-spinner loading-md"></span>
          </div>
        )}

        <div className="h-48 w-full"></div>
      </div>
    </>
  );
}
